import React, { FC, SyntheticEvent, useCallback } from 'react';
import styled from 'styled-components';
import {
  FileMessage,
  MessageDirection,
  setImageViewerInitial,
  TwilioMessage,
} from 'models/twilio';
import clamp from 'lodash/clamp';
import { BEHAVIOR, IE, POSITIONING } from 'components/twilio/constants';
import { renderTimeOnly } from 'components/twilio/utils/renderTime';
import generateMessageId from 'components/twilio/utils/generateMessageId';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

const incomingStyles = {
  wrapper: `
    justify-content: flex-start;
    &:last-child > img {
      border-bottom-left-radius: 0;
    }
  `,
  timestamp: `
    left: 8px;
  `,
};

const outgoingStyles = {
  wrapper: `
    justify-content: flex-end;
    &:last-child > img {
      border-bottom-right-radius: 0;
    }
  `,
  timestamp: `
    right: 8px;
  `,
};

const ImageWrapper = styled.div<{ $isOutgoing: boolean }>`
  position: relative;
  width: ${BEHAVIOR.CHAT_GROUP_MAX_WIDTH};
  margin: 2px 0;

  display: flex;
  ${({ $isOutgoing }) => ($isOutgoing ? outgoingStyles.wrapper : incomingStyles.wrapper)};
`;

const MessageImage = styled.img<{ $isOutgoing: boolean }>`
  display: block;
  position: relative;
  overflow: hidden;

  min-width: 144px;
  min-height: 144px;
  max-width: 100%;
  max-height: 300px;
  ${IE && 'max-width: 300px;'}
  object-fit: cover;

  border-radius: ${POSITIONING.BORDER_RADIUS};
  border: 1px solid rgba(0, 0, 0, 0.1);
  cursor: pointer;
`;

const MessageTimestamp = styled.div<{ $isOutgoing: boolean }>`
  font-size: 11px;
  text-transform: uppercase;
  position: absolute;
  bottom: 8px;
  ${({ $isOutgoing }) => ($isOutgoing ? outgoingStyles.timestamp : incomingStyles.timestamp)};
  color: white;

  margin-top: 2px;
  border-radius: 20px;
  background-color: rgba(0, 0, 0, 0.4);
  padding: 2px 6px;
`;

export interface FileImageMessageProps {
  direction: MessageDirection;
  message: TwilioMessage;
  file: FileMessage;
}

const FileImageMessage: FC<FileImageMessageProps> = ({
  direction,
  message: {
    id,
    index,
    dateCreated,
  },
  file,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const onClick = useCallback(() => {
    dispatch(setImageViewerInitial(id));
  }, [dispatch, id]);

  /**
   * @description IE is inferring the parent's height wrongly, so this function calculates it manually.
   * For IE, the image has: min-height & min-width: 144px, max-height & max-width: 300px.
   * Values below are 2 less than that because of the border.
   * Basically just trying to calculate the height of the image the object-fit: cover polyfill is applied.
   */
  const onImageLoad = useCallback((e: SyntheticEvent<HTMLImageElement>) => {
    if (IE) {
      const imageElement = e.target as HTMLImageElement;
      const { naturalWidth, naturalHeight } = imageElement;

      let scaledHeight = naturalHeight;
      if (naturalWidth > naturalHeight) {
        if (naturalWidth < 142) scaledHeight = 142;
        else if (naturalWidth > 298) scaledHeight = clamp((298 / naturalWidth) * naturalHeight, 142, 298);
        else if (naturalHeight < 142) scaledHeight = 142;
      } else if (naturalWidth < 142) scaledHeight = clamp((142 / naturalWidth) * naturalHeight, 142, 298);
      else if (naturalWidth > 298) scaledHeight = 298;
      else if (naturalHeight > 298) scaledHeight = 298;

      imageElement.parentElement.style.height = `${Math.round(scaledHeight) + 4 + 2}px`; // Margins + Borders
    }
  }, []);

  return (
    <ImageWrapper
      key={generateMessageId(index)}
      id={generateMessageId(index)}
      $isOutgoing={direction === MessageDirection.OUTGOING}
    >
      <MessageImage
        $isOutgoing={direction === MessageDirection.OUTGOING}
        src={file.url}
        alt={file.name}
        onClick={onClick}
        onLoad={onImageLoad}
      />
      <MessageTimestamp $isOutgoing={direction === MessageDirection.OUTGOING}>{renderTimeOnly(dateCreated, t)}</MessageTimestamp>
    </ImageWrapper>
  );
};

export default React.memo(FileImageMessage);
